// src/components/auth/Conta.jsx
//
// Fluxo de cadastro simplificado — apenas PACIENTE.
//
// Fluxo de cadastro:
//   step 1 → CpfBox (verifica CPF)
//   step 2 → CadastroPacienteBox (dados pessoais)
//   step 3 → VerificacaoCode (confirma SMS)
//
// Fluxo de login:
//   step 1 → LoginBox (CPF + envia SMS)
//   step 2 → VerificacaoCode (confirma SMS)

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";

import Tab from "react-bootstrap/Tab";
import Nav from "react-bootstrap/Nav";
import Offcanvas from "../common/SwipeableOffcanvas.jsx";
import PagePoliticas from "../../pages/politicas/PagePoliticas";

import Loading from "../layout/Loading";
import LoginBox from "./LoginBox";
import VerificacaoCode from "./VerificacaoCode";
import CpfBox from "./CpfBox";
import CadastroPacienteBox from "./CadastroPacienteBox";
import GymBrandingBanner from './GymBrandingBanner.jsx';

import { useUser } from "../../contexts/UserContext";

import Style from "../../styles/components/auth/Login.module.css";
import ContaEscuro from "../../Assets/images/logo/contaEscuro.svg";
import Account from "../../Assets/icons/loginAccount.svg";
import AddAccount from "../../Assets/icons/loginAddAccount.svg";
import qrCode from "../../Assets/images/banners/qr-login.png";
import useGymBranding from '../../hooks/useGymBranding';

// Seo — ajuste o caminho conforme onde ficou após reestruturação
import Seo from "../../components/layout/Seo";
import { seo } from "../../data/seoData";

// Estado inicial do formulário — apenas campos de PACIENTE
const INITIAL_FORM = {
  CPF: "",
  NOME: "",
  SOBRENOME: "",
  EMAIL: "",
  CONTATO: "",
  SEXO: "",
  DATA_NASC: "",
  ESTADO: "",
  CIDADE: "",
  DDD: "",
  CELULAR: "",
  // aliases em lowercase usados por alguns subcomponentes
  nome: "",
  sobrenome: "",
  email: "",
  cidade: "",
  estado: "",
  sexo: "",
  nascimento: "",
};

const Conta = () => {
  const { currentUser, loading: authLoading } = useUser();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("login");
  const [step, setStep] = useState(1);
  const [cpf, setCpf] = useState("");
  const [mensagemSistema, setMensagemSistema] = useState("");
  const [erros, setErros] = useState({});
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [showPoliticas, setShowPoliticas] = useState(false);

  // Estado para o fluxo de SMS (Twilio)
  const [sessionId, setSessionId] = useState(null);
  const [hint, setHint] = useState({ emailMasked: null, contatoMasked: null });
  const [confirmationResult, setConfirmationResult] = useState(null);
  // Guarda o último payload enviado ao startVerification para permitir o reenvio
  // (mesmo mode/cpf/formData), evitando que a sessão fique num beco sem saída.
  const [verifyPayload, setVerifyPayload] = useState(null);
const gymBranding = useGymBranding();

  // Detecta se está em mobile (app só funciona em mobile)
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia("(max-width: 950px)").matches
  );

  // Hidrata gymRef da URL → localStorage (fix Safari in-app browser)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gymRef = params.get("gymRef");
    if (!gymRef) return;

    const payload = {
      referralCode: gymRef,
      capturedAt: Number(params.get("gymAt")) || Date.now(),
      origin: "GYM",
      partnerUser: params.get("gymPartner") === "1",
      gymName: params.get("gymName") || null,
      city: params.get("gymCity") || null,
      stateUf: params.get("gymState") || null,
      logoUrl: params.get("gymLogo") || null,
    };

    // Regravar no localStorage para garantir que CadastroPacienteBox leia
    localStorage.setItem("LESVITA_GYM_REF", JSON.stringify(payload));

    // Vínculo de equipe (link /login?...&gymTeam=CODE) → LESVITA_TEAM_JOIN, mesmo
    // padrão do gymRef. Aditivo: só grava se o param existir.
    const gymTeam = params.get("gymTeam");
    if (gymTeam) {
      localStorage.setItem("LESVITA_TEAM_JOIN", JSON.stringify({
        teamCode: gymTeam,
        gymCode: gymRef,
        capturedAt: payload.capturedAt,
      }));
    }

    // Pré-popular formData direto (não depende do useEffect do filho)
    setFormData((prev) => ({
      ...prev,
      GYM_REFERRAL_CODE: gymRef,
      GYM_REFERRAL_CAPTURED_AT: payload.capturedAt,
      GYM_REFERRAL_ORIGIN: "GYM",
      GYM_REFERRAL_GYM_NAME: payload.gymName,   // ← novo
      GYM_REFERRAL_CITY: payload.city,          // ← novo
      GYM_REFERRAL_STATE_UF: payload.stateUf,  // ← novo
      GYM_REFERRAL_LOGO_URL: payload.logoUrl,  // ← novo
      PARTNER_USER: payload.partnerUser,
    }));
  }, []); 

  // Lê ?tab=cadastro da URL para abrir aba correta direto
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    if (tab === "cadastro") {
      setActiveTab("cadastro");
    } else {
      setActiveTab("login");
    }
    setStep(1);
  }, []);

  // Listener de resize para mobile
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 950px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Redireciona se já logado
  useEffect(() => {
    if (!authLoading && currentUser) {
      navigate("/plataforma/pessoal", { replace: true });
    }
  }, [currentUser, authLoading, navigate]);

  // Handler genérico de input — suporta objeto (MULTIPLE_FIELDS) ou campo único
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "MULTIPLE_FIELDS") {
      setFormData((prev) => ({ ...prev, ...value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setStep(1);
    setMensagemSistema("");
    setErros({});
  };

  // Reenvio de código: replica o mesmo payload do startVerification e atualiza
  // sessionId/hint. Retorna { ok } para o VerificacaoCode reagir (sucesso/erro).
  const handleResend = async () => {
    if (!verifyPayload) {
      return { ok: false, message: "Recomece o processo de login/cadastro." };
    }
    try {
      const functions = getFunctions(undefined, "southamerica-east1");
      const startVerification = httpsCallable(functions, "startVerification");
      const res = await startVerification(verifyPayload);
      const d = res?.data || {};
      if (d.status === "sent") {
        setSessionId(d.sessionId);
        setHint(d.hint);
        return { ok: true };
      }
      return { ok: false, message: "Não foi possível reenviar o código." };
    } catch (e) {
      const msg = (e?.message || "").includes("Aguarde")
        ? "Aguarde alguns segundos antes de pedir outro código."
        : "Não foi possível reenviar o código. Tente novamente.";
      return { ok: false, message: msg };
    }
  };

  // Loading enquanto Firebase verifica sessão
  if (authLoading) return <Loading />;

  // Desktop — app ainda não disponível
  if (!isMobile) {
    return (
      <div className={Style.containerMain}>
        <div className={Style.unavailableMessage}>
          <h2>App não disponível para computador ainda</h2>
          <p>Recomendamos que abra o app no seu celular, basta ler o QR Code.</p>
          <div className={Style.contentImg}>
            <img src={qrCode} alt="QR Code LesVita" />
          </div>
        </div>
      </div>
    );
  }

  // Verifica se o painel lateral deve estar expandido
  // No cadastro: step 2 em diante expande o painel esquerdo
  const isExpanded = activeTab === "cadastro" && step >= 2;

  return (
    <>
      <Seo {...seo.login} />
      <div className={Style.containerMain}>
        <div className={Style.BoxMain}>
          <GymBrandingBanner />
          <Tab.Container
            activeKey={activeTab}
            onSelect={handleTabChange}
          >
            {/* ─── Painel esquerdo: navegação + formulário de cadastro ─── */}
            <Nav
              variant="pills"
              className={
                isExpanded
                  ? `${Style.sectionLeft} ${Style.sectionLeftExpanded}`
                  : Style.sectionLeft
              }
            >
              {/* Botões de navegação Login / Criar Conta */}
              <div className={isExpanded ? Style.OptionUserNone : Style.OptionUser}>
                <div className={Style.MainUp}>
                  <Nav.Item className={Style.ContentButConta}>
                    <Nav.Link
                      className={`${Style.butContaUp} ${activeTab === "login" ? Style.active : ""}`}
                      eventKey="login"
                      style={gymBranding?.darkPrimaryColor || gymBranding?.brandPrimaryColor ? {
                        '--gym-tab-active': gymBranding.darkPrimaryColor || gymBranding.brandPrimaryColor
                      } : undefined}
                    >
                      <img style={{ marginBottom: "15px" }} src={Account} alt="Login" />
                      <p>Fazer Login</p>
                    </Nav.Link>
                  </Nav.Item>

                  <Nav.Item className={Style.ContentButConta}>
                    <Nav.Link
                      className={`${Style.butContaDown} ${activeTab === "cadastro" ? Style.active : ""}`}
                      eventKey="cadastro"
                      style={gymBranding?.darkPrimaryColor || gymBranding?.brandPrimaryColor ? {
                        '--gym-tab-active': gymBranding.darkPrimaryColor || gymBranding.brandPrimaryColor
                      } : undefined}
                    >
                      <img style={{ marginBottom: "15px" }} src={AddAccount} alt="Cadastro" />
                      <p>Criar Conta</p>
                    </Nav.Link>
                  </Nav.Item>
                </div>
                <div className={Style.MainDown}>
                  <img src={ContaEscuro} alt="LesVita" />
                </div>
              </div>

              {/* Painel expandido: step 2+ do cadastro */}
              <div
                className={
                  isExpanded
                    ? `${Style.containerBanner} ${Style.bannerExpanded}`
                    : `${Style.containerBanner} ${Style.containerBannerMobile}`
                }
              >
                {/* Step 2 — dados do paciente */}
                {activeTab === "cadastro" && step === 2 && (
                  <CadastroPacienteBox
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handlePreviousStep={() => setStep(1)}
                    setSessionId={setSessionId}
                    setHint={setHint}
                    setStep={setStep}
                    erros={erros}
                    setMensagemSistema={setMensagemSistema}
                    setVerifyPayload={setVerifyPayload}
                  />
                )}

                {/* Step 3 — verificação de código SMS (cadastro) */}
                {activeTab === "cadastro" && step === 3 && (
                  <VerificacaoCode
                    eventKey="cadastro"
                    sessionId={sessionId}
                    hint={hint}
                    setStep={setStep}
                    formData={formData}
                    onResend={handleResend}
                  />
                )}
              </div>
            </Nav>

            {/* ─── Painel direito: login + step 1 do cadastro ─── */}
            <div
              className={
                isExpanded
                  ? `${Style.sectionRight} ${Style.sectionContract}`
                  : Style.sectionRight
              }
            >
              <Tab.Content className={Style.containerLogin}>
                {/* ── Aba Login ── */}
                <Tab.Pane eventKey="login" className={Style.tabPaneMobile}>
                  {step === 1 && (
                    <LoginBox
                      cpf={cpf}
                      setCpf={setCpf}
                      setActiveTab={setActiveTab}
                      setStep={setStep}
                      setConfirmationResult={() =>
                        setConfirmationResult({ cpf })
                      }
                      setFormData={setFormData}
                      setMensagemSistema={setMensagemSistema}
                      mensagemSistema={mensagemSistema}
                      setSessionId={setSessionId}
                      setHint={setHint}
                      setVerifyPayload={setVerifyPayload}
                    />
                  )}
                  {step === 2 && (
                    <VerificacaoCode
                      eventKey="login"
                      sessionId={sessionId}
                      hint={hint}
                      setStep={setStep}
                      confirmationResult={confirmationResult}
                      formData={formData}
                      onResend={handleResend}
                    />
                  )}
                </Tab.Pane>

                {/* ── Aba Cadastro — step 1: apenas CPF ── */}
                <Tab.Pane eventKey="cadastro" className={Style.tabPaneMobile}>
                  <div className={Style.containerSteps}>
                    {step === 1 && (
                      <CpfBox
                        formData={formData}
                        handleInputChange={handleInputChange}
                        handleNextStepCPF={() => setStep(2)}
                        handlePreviousStep={() => {
                          setActiveTab("login");
                          setStep(1);
                        }}
                        setActiveTab={setActiveTab}
                        setStep={setStep}
                        setCpf={setCpf}
                        mensagemSistema={mensagemSistema}
                        setMensagemSistema={setMensagemSistema}
                        setErros={setErros}
                      />
                    )}
                  </div>
                </Tab.Pane>

                {/* Link de políticas */}
                <div className={Style.contentPolicy}>
                  <button
                    className={Style.btnPolicy}
                    onClick={() => setShowPoliticas(true)}
                  >
                    Termos de Uso &amp; Política de Privacidade
                  </button>
                </div>
                <Offcanvas
                  className={Style.containerOffcanvasPolitica}
                  show={showPoliticas}
                  onHide={() => setShowPoliticas(false)}
                  placement="end"
                >
                  <Offcanvas.Header className={Style.offHeader}>
                    <button
                      onClick={() => setShowPoliticas(false)}
                      className={Style.btnsHeaderOff}
                    >
                      <svg
                        width="31"
                        height="19"
                        viewBox="0 0 31 19"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={Style.backArrow}
                      >
                        <path
                          d="M17.1799 17.6508L30.1086 4.66082C30.3231 4.44747 30.4932 4.19375 30.6091 3.91432C30.725 3.63489 30.7845 3.33526 30.784 3.03274C30.7836 2.73021 30.7232 2.43077 30.6065 2.15168C30.4897 1.87259 30.3189 1.61938 30.1038 1.40666C29.6738 0.980473 29.0926 0.74176 28.4871 0.742658C27.8817 0.743555 27.3012 0.98399 26.8725 1.41145L15.431 12.7722L4.07045 1.44525C3.64045 1.01907 3.05927 0.780353 2.45384 0.78125C1.84842 0.782148 1.26795 1.02258 0.839207 1.45004C0.622987 1.66261 0.451097 1.91597 0.333498 2.19544C0.215899 2.47491 0.154931 2.77494 0.154122 3.07815C0.15583 3.38135 0.217687 3.68119 0.336114 3.96032C0.454541 4.23944 0.627181 4.49229 0.844031 4.70421L13.8112 17.6558C14.0261 17.8881 14.2868 18.0734 14.5769 18.1999C14.867 18.3265 15.1801 18.3916 15.4966 18.3911C15.8131 18.3906 16.1261 18.3246 16.4158 18.1972C16.7055 18.0698 16.9657 17.8838 17.1799 17.6508Z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>
                  </Offcanvas.Header>
                  <Offcanvas.Body className={Style.bodyOffcanvasPolitica}>
                    <PagePoliticas />
                  </Offcanvas.Body>
                </Offcanvas>
              </Tab.Content>
            </div>
          </Tab.Container>
        </div>
      </div>
    </>
  );
};

export default Conta;
